import type { Category } from '../api';
import type { FieldName } from './field-groups';

/**
 * Returnerer alle default-felter for en kategori, inklusive arvet fra forfædre.
 *
 * Fx hvis Materialer har defaultFields = ['quantity', 'unit', 'lowStockThreshold']
 * og Papir er en underkategori uden egne defaults, returnerer denne funktion
 * Materialers felter for Papir.
 */
export function effectiveDefaultFields(
  categoryId: number | null | undefined,
  categories: Category[],
): FieldName[] {
  if (!categoryId) return [];
  const byId = new Map(categories.map((c) => [c.id, c]));
  const seen = new Set<number>();
  const result = new Set<string>();
  let current = byId.get(categoryId);
  while (current && !seen.has(current.id)) {
    if (current.defaultFields) {
      for (const f of current.defaultFields) result.add(f);
    }
    seen.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return [...result] as FieldName[];
}

/**
 * Returnerer den nærmeste forfader (eksklusive selv) der har felter i sine
 * direkte defaultFields. Bruges til at vise "Arvet fra X" i kategori-formen.
 */
export function ancestorWithDefaults(
  categoryId: number | null | undefined,
  categories: Category[],
): Category | null {
  if (!categoryId) return null;
  const byId = new Map(categories.map((c) => [c.id, c]));
  const seen = new Set<number>();
  let current = byId.get(categoryId);
  while (current && !seen.has(current.id)) {
    if (current.defaultFields && current.defaultFields.length > 0) return current;
    seen.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return null;
}

/** Returnerer true hvis kategorien har lager-felter (quantity m.fl.) — direkte eller arvet. */
export function hasInventoryFields(
  categoryId: number | null | undefined,
  categories: Category[],
): boolean {
  return effectiveDefaultFields(categoryId, categories).includes('quantity' as FieldName);
}
